// WorkspaceList - user's workspace management page
// Implements US-2.1: Create and List Workspaces
// Implements US-2.2: Join Workspace with Role Assignment (link to join page)
// Implements US-6.3: MinIO Integration and Download Page (export indicators)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import DownloadModal from '../components/DownloadModal';
import styles from './WorkspaceList.module.css';
import { listWorkspaces, createWorkspace } from '../services/workspaceService';
import { listFrameworks } from '../services/frameworkService';
import { WorkspaceListItem, Framework } from '../types';

/**
 * RENDER workspace list page with create functionality
 * - Fetches user's workspaces on mount
 * - Allows creating new workspaces
 * - Navigates to workspace detail on click
 */
const WorkspaceList: React.FC = () => {
  const navigate = useNavigate();

  // STATE management
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([]);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);

  // DOWNLOAD modal state
  const [downloadModalWorkspace, setDownloadModalWorkspace] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // CREATE form state
  const [workspaceName, setWorkspaceName] = useState<string>('');
  const [selectedFramework, setSelectedFramework] = useState<string>('');

  // GET auth state to wait for authentication before fetching
  const { loading: authLoading } = useAuth();

  /**
   * FETCH workspaces and frameworks on component mount
   * WAIT for authentication to complete before fetching to prevent race condition
   */
  useEffect(() => {
    // SKIP fetching if auth is still loading
    if (authLoading) {
      return;
    }

    fetchData();
  }, [authLoading]);

  /**
   * FETCH workspaces and frameworks from API
   */
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // FETCH workspaces and frameworks in parallel
      const [workspacesResponse, frameworksResponse] = await Promise.all([
        listWorkspaces(),
        listFrameworks(),
      ]);

      if (workspacesResponse.success) {
        setWorkspaces(workspacesResponse.workspaces);
      } else {
        throw new Error(workspacesResponse.error || 'Workspaces konnten nicht geladen werden');
      }

      if (frameworksResponse.success) {
        setFrameworks(frameworksResponse.frameworks);
      } else {
        throw new Error(frameworksResponse.error || 'Frameworks konnten nicht geladen werden');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  /**
   * HANDLE workspace creation
   * - Validates input
   * - Creates workspace via API
   * - Navigates to new workspace
   */
  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();

    // VALIDATE form inputs
    if (!workspaceName.trim()) {
      setError('Workspace-Name ist erforderlich');
      return;
    }

    if (workspaceName.length > 200) {
      setError('Workspace-Name darf maximal 200 Zeichen lang sein');
      return;
    }

    if (!selectedFramework) {
      setError('Bitte wähle ein Framework');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      // CREATE workspace
      const response = await createWorkspace({
        name: workspaceName,
        frameworkSlug: selectedFramework,
      });

      if (response.success) {
        // NAVIGATE to new workspace
        navigate(`/workspaces/${response.workspace.id}`);
      } else {
        throw new Error(response.error || 'Workspace konnte nicht erstellt werden');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein unerwarteter Fehler ist aufgetreten');
      setCreating(false);
    }
  };

  /**
   * HANDLE workspace card click
   * - Navigates to workspace detail page
   */
  const handleWorkspaceClick = (workspaceId: string) => {
    navigate(`/workspaces/${workspaceId}`);
  };

  /**
   * HANDLE create modal open
   */
  const handleOpenCreateModal = () => {
    setWorkspaceName('');
    setSelectedFramework('');
    setError(null);
    setShowCreateModal(true);
  };

  /**
   * HANDLE create modal close
   */
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setWorkspaceName('');
    setSelectedFramework('');
    setError(null);
  };

  /**
   * HANDLE download modal open
   * - Stops event propagation to prevent workspace navigation
   */
  const handleOpenDownloadModal = (
    e: React.MouseEvent,
    workspaceId: string,
    workspaceName: string
  ) => {
    e.stopPropagation();
    setDownloadModalWorkspace({ id: workspaceId, name: workspaceName });
  };

  /**
   * HANDLE download modal close
   */
  const handleCloseDownloadModal = () => {
    setDownloadModalWorkspace(null);
  };

  /**
   * FORMAT date string to readable format
   */
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * RENDER header content for Layout
   */
  const header = (
    <>
      <div className={styles.headerLeft}>
        <img
          src={require('../assets/insurtale-logo.webp')}
          alt="insur_tale logo"
          className={styles.logo}
        />
        <h1 className={styles.appName}>Arbiter</h1>
      </div>
      <div className={styles.headerActions}>
        <button
          className={styles.joinButton}
          onClick={() => navigate('/join')}
          disabled={loading}
        >
          Mit Code beitreten
        </button>
        <button
          className={styles.createButton}
          onClick={handleOpenCreateModal}
          disabled={loading}
        >
          + Workspace erstellen
        </button>
      </div>
    </>
  );

  // RENDER loading state
  if (loading) {
    return (
      <Layout header={header}>
        <div className={styles.loadingState}>
          <p>Lade Workspaces...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout header={header}>
      {/* PAGE title and subtitle */}
      <div className={styles.pageHeader}>
        <h2 className={styles.pageTitle}>Meine Workspaces</h2>
        <p className={styles.pageSubtitle}>Verwalte deine Arbiter Sessions</p>
      </div>

      {/* ERROR message */}
      {error && (
        <div className={styles.errorNotice}>
          <strong>Fehler:</strong> {error}
        </div>
      )}

      {/* WORKSPACE grid or empty state */}
      {workspaces.length > 0 ? (
        <div className={styles.workspaceGrid}>
          {workspaces.map((workspace) => (
            <div
              key={workspace.id}
              className={`${styles.workspaceCard} ${
                workspace.status === 'closed' ? styles.closed : ''
              }`}
              onClick={() => handleWorkspaceClick(workspace.id)}
            >
              {/* WORKSPACE title */}
              <h2 className={styles.workspaceTitle}>{workspace.name}</h2>

              {/* WORKSPACE info */}
              <div className={styles.workspaceInfo}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Framework:</span>
                  <span>{workspace.framework.name}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Raum Code:</span>
                  <span className={styles.roomCode}>{workspace.roomCode}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Elemente:</span>
                  <span>{workspace._count.items}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Erstellt:</span>
                  <span>{formatDate(workspace.createdAt)}</span>
                </div>
              </div>

              {/* STATUS badges and actions */}
              <div className={styles.cardFooter}>
                <div className={styles.badges}>
                  <span
                    className={`${styles.badge} ${
                      workspace.status === 'closed' ? styles.badgeClosed : styles.badgeActive
                    }`}
                  >
                    {workspace.status === 'active' ? 'Aktiv' : 'Geschlossen'}
                  </span>
                  {workspace.exported && (
                    <span className={`${styles.badge} ${styles.badgeExported}`}>
                      Exportiert
                    </span>
                  )}
                </div>
                {workspace.exported && (
                  <button
                    className={styles.downloadsButton}
                    onClick={(e) => handleOpenDownloadModal(e, workspace.id, workspace.name)}
                    aria-label="Downloads anzeigen"
                  >
                    Downloads
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* EMPTY state */
        <div className={styles.emptyState}>
          <h3>Noch keine Workspaces</h3>
          <p>Erstelle deinen ersten Workspace, um loszulegen!</p>
          <button
            className={styles.createButton}
            onClick={handleOpenCreateModal}
          >
            Ersten Workspace erstellen
          </button>
        </div>
      )}

      {/* CREATE workspace modal */}
      {showCreateModal && (
        <div className={styles.modalOverlay} onClick={handleCloseCreateModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Neuen Workspace erstellen</h2>

            <form onSubmit={handleCreateWorkspace}>
              {/* WORKSPACE name input */}
              <div className={styles.formGroup}>
                <label htmlFor="workspaceName" className={styles.label}>
                  Workspace-Name <span className={styles.required}>*</span>
                </label>
                <input
                  id="workspaceName"
                  type="text"
                  className={styles.input}
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="z.B. Q4 Feature-Priorisierung"
                  maxLength={200}
                  autoFocus
                  disabled={creating}
                />
                <p className={styles.helperText}>
                  {workspaceName.length}/200 Zeichen
                </p>
              </div>

              {/* FRAMEWORK selection */}
              <div className={styles.formGroup}>
                <label htmlFor="framework" className={styles.label}>
                  Framework <span className={styles.required}>*</span>
                </label>
                <select
                  id="framework"
                  className={styles.select}
                  value={selectedFramework}
                  onChange={(e) => setSelectedFramework(e.target.value)}
                  disabled={creating}
                >
                  <option value="">Framework auswählen...</option>
                  {frameworks.map((framework) => (
                    <option key={framework.id} value={framework.slug}>
                      {framework.name}
                    </option>
                  ))}
                </select>
                {selectedFramework && (
                  <p className={styles.helperText}>
                    {frameworks.find((f) => f.slug === selectedFramework)?.description}
                  </p>
                )}
              </div>

              {/* MODAL actions */}
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleCloseCreateModal}
                  disabled={creating}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={creating}
                >
                  {creating ? 'Erstelle...' : 'Workspace erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DOWNLOAD modal */}
      {downloadModalWorkspace && (
        <DownloadModal
          workspaceId={downloadModalWorkspace.id}
          workspaceName={downloadModalWorkspace.name}
          onClose={handleCloseDownloadModal}
        />
      )}
    </Layout>
  );
};

export default WorkspaceList;
